import { NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { Component, Input, ViewChild, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomToolKitParam, ToolConfigCustomToolKit, ToolConfigEnumParam, isToolConfigEnumParam } from '@shared/index';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from "primeng/inputtext";
import { Menu } from 'primeng/menu';
import { MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { CodemirrorAiComponent } from "../../../../../../../shared/components/ai-bot/utils/codemirror-ai/codemirror-ai.component";
import { FunctionNameSuggestComponent } from '../../../../../../../shared/components/ai-bot/utils/function-name-suggest/function-name-suggest.component';
import { HintComponent } from "@shared-angular/components/hint/hint.component";
import { SkillIdInputComponent } from '../../../../../../../shared/components/ai-bot/utils/skill-id-input/skill-id-input.component';
import { DividerModule } from 'primeng/divider';

@Component({
  selector: 'app-ifthen-tab-detail',
  imports: [NgClass, SlicePipe, NgTemplateOutlet, 
    FormsModule,
    SkillIdInputComponent,
    FunctionNameSuggestComponent,
    CodemirrorAiComponent,
    HintComponent,
    GotoBlocklyDirective,
    InputText,
    ToggleSwitchModule,
    InputGroupModule,
    InputGroupAddonModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    TabsModule,
    MenuModule,
    SelectModule,
    SelectButtonModule,
    TableModule,
    MessageModule,
    DividerModule
  ],
  templateUrl: './ifthen-tab-detail.component.html',
  styleUrl: './ifthen-tab-detail.component.scss'
})
export class IfthenTabDetailComponent {


  @Input({ required: true }) public selectedToolId!: string;
  public readonly closeDrawer = output();
  public selectedParamIndex: number = -1;
  public toolCallAnswerShowIndex: number = 0;
  public paramForNewForm?: CustomToolKitParam = undefined;
  public readonly linebreak = '\n';
  public examplePLaceholder = $localize`:@@example-placeholder:Beispielsweise:${this.linebreak} ‣ Sage dem user dass...${this.linebreak} ‣ Reagiere auf die Frage des Users mit...${this.linebreak} ‣ sei freundlich und hilfreich...`;
  public humanFeedback = $localize`:@@active-human-feeback:Menschliches Feedback aktivieren`;
  public requiredOptions = [
    { label: $localize`:@@no:Nein`, value: true },
    { label: $localize`:@@yes:Ja`, value: false }
  ];
  public paramTypeOptions: { label: string, value: CustomToolKitParam['type'] }[] = [
    { label: $localize`:@@text:Text`, value: 'string' },
    { label: $localize`:@@number:Zahl`, value: 'number' },
    { label: $localize`:@@true-false:wahr / falsch`, value: 'boolean' },
    { label: $localize`:@@list-selection:Auswahl aus Liste`, value: 'enum' }
  ];


  constructor(
    public rss: RouteShareService,
    protected stageAdmin: StageAdminService,
    public editorItemService: EditorItemsService
  ) { }


  public get selectedParam(): CustomToolKitParam | undefined {
    return this.selectedTool?.params[this.selectedParamIndex];
  }

  public get selectedTool(): ToolConfigCustomToolKit | undefined {
    return this.customToolKits.find((tc) => tc.id === this.selectedToolId);
  }

  public get customToolKits(): ToolConfigCustomToolKit[] {
    return this.rss.botConfig.toolConfigs.filter((tc) => tc.tool === 'customtoolkit') as ToolConfigCustomToolKit[];
  }


  public selectPrevRule() {
    const currentIndex = this.customToolKits.findIndex(tc => tc.id === this.selectedToolId);
    let newIndex = currentIndex - 1;
    if (newIndex === -1) { newIndex = this.customToolKits.length - 1; }
    const newTool = this.customToolKits[newIndex];
    if (newTool) {
      this.selectedToolId = newTool.id;
      this.selectedParamIndex = -1;
    }
  }

  public selectNextRule() {
    const currentIndex = this.customToolKits.findIndex(tc => tc.id === this.selectedToolId);
    let newIndex = currentIndex + 1;
    if (newIndex >= this.customToolKits.length) { newIndex = 0; }
    const newTool = this.customToolKits[newIndex];
    if (newTool) {
      this.selectedToolId = newTool.id;
      this.selectedParamIndex = -1;
    }
  }


  public selectEmptyParam() {
    this.selectedParamIndex = -1;
    this.paramForNewForm = {
      description: '',
      required: true,
      type: 'string',
      key: ''
    };
  }



  public deleteParamFromList(index: number) {
    this.selectedTool?.params.splice(index, 1);
    void this.rss.saveBotConfigField('toolConfigs');
  }


  public getSkillNames(): string[] {
    const skillsWithSkillId: string[] = [];
    const toolsWithSkillId = this.rss.botConfig.toolConfigs.filter(toolConfig => toolConfig.skillId !== '');
    skillsWithSkillId.push(...toolsWithSkillId.map(toolConfig => toolConfig.skillId + ''));
    const docsWithSkillId = this.rss.documents.filter(doc => doc.skillId !== '');
    skillsWithSkillId.push(...docsWithSkillId.map(doc => doc.skillId + ''));
    const datatrunksWithSkillId = this.rss.datatrunks.filter(datatrunk => datatrunk.skillId !== '');
    skillsWithSkillId.push(...datatrunksWithSkillId.map(datatrunk => datatrunk.skillId + ''));
    const webscrapesWithSkillId = this.rss.webscrapes.filter(webscrape => webscrape.skillId !== '');
    skillsWithSkillId.push(...webscrapesWithSkillId.map(webscrape => webscrape.skillId + ''));
    return skillsWithSkillId;
  }

  public getToolbarVariables(): string[] {
    const variableNames = this.selectedTool?.params.map((param: CustomToolKitParam) => param.key);
    if (variableNames) {
      return variableNames;
    }
    return [];

  }

  public addParamToList() {
    if (this.paramForNewForm === undefined) return;
    this.paramForNewForm.key = this.paramForNewForm.key.trim();
    this.paramForNewForm.description = this.paramForNewForm.description.trim();
    this.selectedTool?.params.unshift(this.paramForNewForm);
    void this.rss.saveBotConfigField('toolConfigs');
    this.paramForNewForm = undefined;
  }

  public getSelectedParamState() {
    if (this.selectedTool && this.selectedParam) {
      if (this.selectedTool.params.includes(this.selectedParam)) {
        return "existing";
      } else {
        return "new";
      }
    }
    return "new";
  }

  public isToolValid(): boolean {
    if (this.selectedTool?.name.trim() == '') return false;
    if (this.selectedTool?.skillId.trim() == '') return false;
    if (this.selectedTool?.description.trim() == '') return false;
    return true;
  }

  public isParamValid(): boolean {
    if (this.selectedParam?.key.trim() == '') return false;
    if (this.selectedParam?.description.trim() == '') return false;
    return true;
  }



  public hasAtleastOneToolCallAnswer(): boolean {
    if (!this.selectedTool) { return false; }
    if (this.selectedTool.toolCallAnswer.length === 0) { return false; }
    if (this.selectedTool.toolCallAnswer.length == 1 && this.selectedTool.toolCallAnswer[0] === '') {
      return false;
    }
    return true;
  }


  public formularModelChange() {
    // soll nicht ausgeführ werden wenn wir ein "neues" Element hinzufügen
    // da tut das der button
    if (this.getSelectedParamState() === "new") return;
    if (!this.isParamValid()) { return; }
    void this.rss.saveBotConfigField('toolConfigs');
  }

  public onNameFinish(name: string): void {
    if (!this.selectedTool) return;
    this.selectedTool.name = name;
    if (this.selectedTool.skillId === '' && name.trim() !== '') {
      this.selectedTool.skillId = this.deriveSkillId(name);
    }
    void this.rss.saveBotConfigField('toolConfigs');
  }

  public onNameChange(name: string): void {
    if (!this.selectedTool) return;
    if (this.selectedTool.skillId === '' && name.trim() !== '') {
      this.selectedTool.skillId = this.deriveSkillId(name);
    }
    if (!this.rss.checkFunctionName(name)) return;
    void this.rss.saveBotConfigField('toolConfigs');
  }

  private deriveSkillId(from: string): string {
    return from.replace(/[^a-zA-Z0-9]/g, '').replace(/^\d+/, '').slice(0, 20);
  }

  public setParamRequired(data: CustomToolKitParam, required: boolean): void {
    data.required = required;
    this.formularModelChange();
  }

  public setParamType(data: CustomToolKitParam, type: CustomToolKitParam['type']): void {
    data.type = type;
    if (type === 'enum') {
      this.changeParamType();
    }
    this.formularModelChange();
  }

  protected changeParamType(): void {
    if (this.paramForNewForm && isToolConfigEnumParam(this.paramForNewForm)) {
      this.paramForNewForm.values = [];
    } else if (this.selectedParam && isToolConfigEnumParam(this.selectedParam)) {
      this.selectedParam.values = [];
    }
  }

  public deleteToolAnswerAtIndex(index: number) {
    if (this.selectedTool?.toolCallAnswer && this.selectedTool.toolCallAnswer.length > 1) {
      this.selectedTool.toolCallAnswer.splice(index, 1);

      if (this.toolCallAnswerShowIndex >= this.selectedTool.toolCallAnswer.length) {
        this.toolCallAnswerShowIndex = this.selectedTool.toolCallAnswer.length - 1;
      }
      void this.rss.saveBotConfigField('toolConfigs');
    }
  }

  public enumValChanged(data: ToolConfigEnumParam, e: Event) {
    data.values = (e.target as HTMLInputElement).value.split(",");
    void this.rss.saveBotConfigField('toolConfigs');
  }

  public activeTab: string = 'classicAnswers';
  public toolAnswerMenuItems: MenuItem[] = [];

  public answerModeOptions = [
    {
      label: $localize`:@@selection-seq:Nacheinander bis zum letzten`,
      value: 'sequence'
    },
    {
      label: $localize`:@@selection-seqloop:Nacheinander (dann wieder von vorne)`,
      value: 'sequenceloop'
    },
    {
      label: $localize`:@@selection-random:Zufällige Reihenfolge`,
      value: 'random'
    }
  ];

  public openToolAnswerMenu(event: Event, menu: Menu) {
    this.toolAnswerMenuItems = this.buildToolAnswerMenuItems();
    menu.toggle(event);
  }

  private buildToolAnswerMenuItems(): MenuItem[] {
    if (!this.selectedTool) return [];

    const items: MenuItem[] = this.selectedTool.toolCallAnswer.map((answer: string, index: number) => ({
      label: `#${(index + 1).toString()} ${answer.length > 30 ? answer.slice(0, 30) + '...' : answer
        }`,
      command: () => {
        this.toolCallAnswerShowIndex = index;
      }
    }));

    items.push({ separator: true });

    items.push({
      label: $localize`:@@new-entry:Neuer Eintrag`,
      icon: 'ri ri-add-line',
      command: () => {
        if (!this.selectedTool) return;
        this.selectedTool.toolCallAnswer.push('');
        this.toolCallAnswerShowIndex = this.selectedTool.toolCallAnswer.length - 1;
        void this.rss.saveBotConfigField('toolConfigs');
      }
    });

    return items;
  }

  public paramMenuItems: MenuItem[] = [];
  @ViewChild('paramMenu') public paramMenuRef!: Menu;

  public openParamMenu(event: Event, index: number): void {
    this.paramMenuItems = [
      {
        label: $localize`:@@edit:Bearbeiten`,
        icon: 'ri ri-pencil-fill',
        command: () => {
          this.selectedParamIndex = index;
          this.paramForNewForm = undefined;
        }
      },
      {
        label: $localize`:@@delete:Löschen`,
        icon: 'ri ri-close-large-fill',
        command: () => {
          this.deleteParamFromList(index);
        }
      }
    ];
    this.paramMenuRef.toggle(event);
  }
}
